/**
 * ═══════════════════════════════════════════════════════════════════════════
 * LOS RANGOS DEL CHOFER — la tabla que ve la app
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Petición del fundador: «el rol de los rangos, que van subiendo los
 * conductores de nivel por viaje cumplido y por los buenos comentarios de los
 * usuarios; los que comienzan bronce, plata, así sucesivamente».
 *
 * ⚠️ ESTA TABLA TIENE QUE SER LA MISMA QUE `functions/src/niveles.ts`.
 *
 * No se puede importar aquella: vive en otro paquete (`functions/`) y no entra
 * en el bundle de la app. Así que está copiada, y conviene saber qué pasa si
 * un día se separan:
 *
 *   · El RANGO que se enseña NO se calcula aquí. Lo calcula el servidor y lo
 *     guarda en `users/{uid}.nivel`; la app solo lo pinta. O sea que la
 *     medalla SIEMPRE es la correcta, aunque esta tabla se quede vieja.
 *   · Lo único que saldría mal es el «te faltan N viajes» de la barra. Molesto,
 *     pero no miente sobre lo que la persona YA tiene.
 *
 * Esa es la razón de que la copia sea aceptable: el dato que importa es del
 * servidor y este archivo solo adorna.
 */

export type NivelChofer = 'BRONCE' | 'PLATA' | 'ORO' | 'PLATINO' | 'DIAMANTE';

export type Escalon = {
  nivel: NivelChofer;
  viajes: number;
  nota: number;
  minNotas: number;
  color: string;
  emoji: string;
  /** Qué se gana al llegar. Hoy es reconocimiento, y se dice tal cual. */
  queDa: string;
};

/**
 * De mayor a menor, igual que en el servidor: se recorre de arriba abajo y el
 * primero que se cumple es el nivel.
 *
 * Los rangos son VISIBLES Y NADA MÁS — decisión del fundador. No dan descuento
 * en la cuota ni prioridad en el reparto de viajes: eso sería castigar al que
 * empieza, y el que empieza es justo el que necesita trabajar.
 */
export const ESCALONES: Escalon[] = [
  { nivel: 'DIAMANTE', viajes: 750, nota: 4.9, minNotas: 100, color: '#4FC3F7', emoji: '💎',
    queDa: 'El rango más alto de Go Safely. Muy pocos llegan aquí.' },
  { nivel: 'PLATINO', viajes: 300, nota: 4.8, minNotas: 50, color: '#B0BEC5', emoji: '🏆',
    queDa: 'Estás entre los mejores de la plataforma.' },
  { nivel: 'ORO', viajes: 100, nota: 4.7, minNotas: 20, color: '#FFC107', emoji: '🥇',
    queDa: 'Ya eres un chofer con historial. Los pasajeros lo ven en tu ficha.' },
  { nivel: 'PLATA', viajes: 25, nota: 4.5, minNotas: 5, color: '#90A4AE', emoji: '🥈',
    queDa: 'Dejaste de ser nuevo: tu ficha ya lo dice.' },
  { nivel: 'BRONCE', viajes: 0, nota: 0, minNotas: 0, color: '#A1887F', emoji: '🥉',
    queDa: 'Todos empezamos aquí. Cada viaje cuenta.' },
];

export const escalonDe = (nivel: string | null | undefined): Escalon =>
  ESCALONES.find((e) => e.nivel === String(nivel ?? '').toUpperCase()) ??
  ESCALONES[ESCALONES.length - 1];

/**
 * Qué le falta para el siguiente rango.
 *
 * Devuelve null cuando ya está en lo más alto — y entonces la barra no se
 * pinta: una barra al 100% que no sube nunca más parece algo roto.
 */
export function loQueFalta(
  nivelActual: string | null | undefined,
  viajes: number,
  nota: number | null,
  cuentaNotas: number
): { siguiente: Escalon; viajesQueFaltan: number; notaQueHaceFalta: number | null; notasQueFaltan: number } | null {
  const i = ESCALONES.findIndex((e) => e.nivel === String(nivelActual ?? 'BRONCE').toUpperCase());
  const indiceActual = i < 0 ? ESCALONES.length - 1 : i;
  if (indiceActual === 0) return null; // ya es DIAMANTE
  const siguiente = ESCALONES[indiceActual - 1];
  return {
    siguiente,
    viajesQueFaltan: Math.max(0, siguiente.viajes - viajes),
    // Si ya la cumple, no se le pide nada: null significa «esto ya está».
    notaQueHaceFalta: (nota ?? 0) >= siguiente.nota ? null : siguiente.nota,
    notasQueFaltan: Math.max(0, siguiente.minNotas - cuentaNotas),
  };
}
